import { useMemo } from "react";
import { getColorFromCssVariable } from "ui";
import { BarChartCard } from "./shared/BarChartCard";
import { MetricDefinitions } from "./shared/MetricDefinitions";
import { useDatadogQuery } from "../DatadogAnalytics/useDatadogQuery";

type TransactionsData = Record<
  string,
  { success_count: number; failed_count: number }
>;

export function TotalTransactionsChart() {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<TransactionsData>({
      chartType: "TOTAL_TRANSACTIONS",
      delayMs: 600,
    });

  const { labels, successData, failedData } = useMemo(() => {
    const labels: string[] = [];
    const successData: number[] = [];
    const failedData: number[] = [];

    if (data) {
      Object.entries(data).forEach(([timestamp, entry]) => {
        labels.push(timestamp);
        successData.push(entry.success_count);
        failedData.push(entry.failed_count);
      });
    }

    return { labels, successData, failedData };
  }, [data]);

  const { successColor, failedColor } = useMemo(
    () => ({
      successColor: getColorFromCssVariable("--chart-categorical-2-subtler"),
      failedColor: getColorFromCssVariable("--chart-categorical-3-subtle"),
    }),
    []
  );

  return (
    <BarChartCard
      ref={ref}
      title="Total transactions"
      description={
        <MetricDefinitions
          summary="Number of transactions processed per day in the selected period, split by final outcome."
          definitions={[
            {
              term: "Successful",
              description:
                "Number of transactions that resulted in a successful payment.",
            },
            {
              term: "Failed",
              description:
                "Number of transactions that did not result in a successful payment.",
            },
          ]}
        />
      }
      format="number"
      labels={labels}
      queryStatus={queryStatus}
      error={error}
      retry={refetch}
      datasets={[
        {
          label: "SUCCESSFUL",
          data: successData,
          backgroundColor: successColor,
          borderColor: successColor,
          stack: "stack0",
        },
        {
          label: "FAILED",
          data: failedData,
          backgroundColor: failedColor,
          borderColor: failedColor,
          stack: "stack0",
        },
      ]}
    />
  );
}
